import { useState } from 'react';
import { ArrowLeft, Shield, Database, Eye, Trash2, ExternalLink, Loader2, AlertTriangle } from 'lucide-react';

/** Base URL for legal pages on the web (privacy, security, terms). Set VITE_LEGAL_BASE_URL in .env. */
const LEGAL_BASE_URL = import.meta.env.VITE_LEGAL_BASE_URL || 'https://www.luma-todo.xyz';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { useToast } from '@/hooks/use-toast';

interface PrivacySectionProps {
  onBack: () => void;
}

export function PrivacySection({ onBack }: PrivacySectionProps) {
  const { user, signOut } = useAuth();
  const { toast } = useToast();
  const [confirmText, setConfirmText] = useState('');
  const [isDeleting, setIsDeleting] = useState(false);
  const [showFinalConfirm, setShowFinalConfirm] = useState(false);

  const handleDeleteAccount = async () => {
    if (!user) return;

    setIsDeleting(true);
    try {
      const { data: sessionData } = await supabase.auth.getSession();
      const accessToken = sessionData?.session?.access_token;

      if (!accessToken) {
        throw new Error('No valid session');
      }

      const response = await supabase.functions.invoke('delete-account', {
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      });

      if (response.error) {
        throw new Error(response.error.message || 'Failed to delete account');
      }

      toast({
        title: 'Account deleted',
        description: 'Your account and personal data have been permanently deleted.',
      });

      // Sign out and redirect
      await signOut();
      window.location.href = '/';
    } catch (error) {
      console.error('Error deleting account:', error);
      toast({
        title: 'Error',
        description: 'Failed to delete account. Please try again or contact support.',
        variant: 'destructive',
      });
    } finally {
      setIsDeleting(false);
      setShowFinalConfirm(false);
      setConfirmText('');
    }
  };

  const isConfirmValid = confirmText.toLowerCase() === 'delete my account';

  return (
    <div className="h-full flex flex-col">
      {/* Header */}
      <div className="p-4 border-b flex items-center gap-3">
        <Button
          variant="ghost"
          size="icon"
          onClick={onBack}
          className="shrink-0"
        >
          <ArrowLeft className="h-5 w-5" />
        </Button>
        <h2 className="text-lg font-semibold">Privacy</h2>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-auto p-6 space-y-6">
        {/* Data Collection */}
        <div className="space-y-4">
          <div className="flex items-center gap-2">
            <Database className="h-5 w-5 text-muted-foreground" />
            <h3 className="font-medium">Data Collection</h3>
          </div>
          
          <div className="space-y-3 pl-7">
            <p className="text-sm text-muted-foreground">
              We collect only the data necessary to provide the Luma service:
            </p>
            <ul className="text-sm text-muted-foreground space-y-2 list-disc list-inside">
              <li>Your email address for authentication</li>
              <li>Tasks and notes you create</li>
              <li>Focus session data for productivity tracking</li>
            </ul>
          </div>
        </div>

        <Separator />

        {/* Data Security */}
        <div className="space-y-4">
          <div className="flex items-center gap-2">
            <Shield className="h-5 w-5 text-muted-foreground" />
            <h3 className="font-medium">Data Security</h3>
          </div>
          
          <div className="space-y-3 pl-7">
            <p className="text-sm text-muted-foreground">
              Your data is protected with:
            </p>
            <ul className="text-sm text-muted-foreground space-y-2 list-disc list-inside">
              <li>End-to-end encryption in transit</li>
              <li>Secure cloud storage</li>
              <li>Row-level security policies</li>
              <li>Regular security audits</li>
            </ul>
          </div>
        </div>

        <Separator />

        {/* Data Access */}
        <div className="space-y-4">
          <div className="flex items-center gap-2">
            <Eye className="h-5 w-5 text-muted-foreground" />
            <h3 className="font-medium">Your Data Rights</h3>
          </div>
          
          <div className="space-y-3 pl-7">
            <p className="text-sm text-muted-foreground">
              You have the right to:
            </p>
            <ul className="text-sm text-muted-foreground space-y-2 list-disc list-inside">
              <li>Access all your personal data</li>
              <li>Export your data at any time</li>
              <li>Request data deletion</li>
              <li>Withdraw consent for data processing</li>
            </ul>
          </div>
        </div>

        <Separator />

        {/* Legal Pages Links – open on the web app */}
        <div className="space-y-3">
          <h3 className="font-medium">Legal Documents</h3>
          <div className="flex flex-col gap-2 pl-0">
            <a
              href={`${LEGAL_BASE_URL}/privacy`}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-2 text-sm text-primary hover:underline"
            >
              Privacy Policy
              <ExternalLink className="h-3 w-3" />
            </a>
            <a
              href={`${LEGAL_BASE_URL}/security`}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-2 text-sm text-primary hover:underline"
            >
              Security Information
              <ExternalLink className="h-3 w-3" />
            </a>
            <a
              href={`${LEGAL_BASE_URL}/terms`}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-2 text-sm text-primary hover:underline"
            >
              Terms of Service
              <ExternalLink className="h-3 w-3" />
            </a>
          </div>
        </div>

        <Separator />

        {/* Delete Account */}
        <div className="space-y-4">
          <div className="flex items-center gap-2">
            <Trash2 className="h-5 w-5 text-destructive" />
            <h3 className="font-medium text-destructive">Delete Account</h3>
          </div>
          
          <div className="space-y-3 pl-7">
            <p className="text-sm text-muted-foreground">
              Permanently delete your account and personal data. We may retain anonymized 
              usage data and legally required financial records.
            </p>
            
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button variant="destructive" className="w-full">
                  Delete Account
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent className="max-w-md">
                <AlertDialogHeader>
                  <AlertDialogTitle className="flex items-center gap-2">
                    <AlertTriangle className="h-5 w-5 text-destructive" />
                    Delete your account?
                  </AlertDialogTitle>
                  <AlertDialogDescription className="space-y-3">
                    <p>
                      This action <strong className="text-foreground">cannot be undone</strong>. 
                      This will permanently:
                    </p>
                    <ul className="list-disc list-inside space-y-1 text-sm">
                      <li>Delete your authentication credentials</li>
                      <li>Delete your profile information</li>
                      <li>Delete all your tasks and notes</li>
                      <li>Anonymize your focus session history</li>
                    </ul>
                    <p className="text-xs text-muted-foreground mt-2">
                      Financial records may be retained by payment processors for legal compliance.
                    </p>
                  </AlertDialogDescription>
                </AlertDialogHeader>
                
                {!showFinalConfirm ? (
                  <AlertDialogFooter>
                    <AlertDialogCancel>Cancel</AlertDialogCancel>
                    <Button 
                      variant="destructive"
                      onClick={() => setShowFinalConfirm(true)}
                    >
                      I understand, continue
                    </Button>
                  </AlertDialogFooter>
                ) : (
                  <div className="space-y-4">
                    <div className="space-y-2">
                      <Label htmlFor="confirmDelete" className="text-sm">
                        Type <strong className="text-foreground">delete my account</strong> to confirm:
                      </Label>
                      <Input
                        id="confirmDelete"
                        value={confirmText}
                        onChange={(e) => setConfirmText(e.target.value)}
                        placeholder="delete my account"
                        className="font-mono"
                        autoComplete="off"
                      />
                    </div>
                    <AlertDialogFooter>
                      <AlertDialogCancel 
                        onClick={() => {
                          setShowFinalConfirm(false);
                          setConfirmText('');
                        }}
                      >
                        Cancel
                      </AlertDialogCancel>
                      <Button
                        variant="destructive"
                        disabled={!isConfirmValid || isDeleting}
                        onClick={handleDeleteAccount}
                      >
                        {isDeleting ? (
                          <>
                            <Loader2 className="h-4 w-4 animate-spin mr-2" />
                            Deleting...
                          </>
                        ) : (
                          'Permanently Delete Account'
                        )}
                      </Button>
                    </AlertDialogFooter>
                  </div>
                )}
              </AlertDialogContent>
            </AlertDialog>
          </div>
        </div>
      </div>
    </div>
  );
}
